/**
 * 海报创建页 —— UX-1(2026-05-30)。
 *
 * - 「自由创作」:URL `?tab=`(text|i2i)驱动,文字 / 图改图 两 tab 共享 `FreeCreateBody`(不卸载,prompt 保留)。
 * - 波A:普通海报「套模板」路删除 —— `?templateId=` 旧深链一律落自由创作
 *   (「模板」概念已整体迁成长图模板,套用走 `/ai-haibao/detail-long` 骨架预填)。
 *
 * 替代原 FreeCreatePage(整页) + ApplyTemplateSheet(右侧抽屉)两套 chrome。
 */
import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import { PosterCreateShell, type CreateTab } from "../ui/components/create/PosterCreateShell";
import { FreeCreateBody } from "../ui/components/create/FreeCreateBody";

function parseTab(raw: string | null): CreateTab {
  return raw === "i2i" ? "i2i" : "text";
}

export default function PosterCreatePage() {
  const [params, setParams] = useSearchParams();

  // 自由创作:文字 / 图改图。段控渲染在 FreeCreateBody 顶部工具条,这里只负责 URL ?tab 回写。
  const tab = parseTab(params.get("tab"));
  const onModeChange = (next: "T2I" | "I2I") => {
    const p = new URLSearchParams(params);
    p.set("tab", next === "I2I" ? "i2i" : "text");
    setParams(p);
  };

  /**
   * 2026-08-01 —— 页头要认用途,而用途住在 `FreeCreateBody` 里(`useFreeCreateForm`
   * 是普通 useState,不是共享 store,外壳读不到)。
   *
   * ★所以由 body 消费完 `location.state.scenePreset` 后**报上来**,而不是让外壳
   *   再解析一遍那个 state:body 消费时会 `clearSceneCardState()` 清 history state,
   *   第二个读者拿到什么取决于 effect 先后 —— 那种依赖执行顺序的读法迟早翻车。
   *
   * ★★用途还要**写进 URL**(`?scene=`)。原来它只活在内存里:同一个
   *   `/ai-haibao/create`,点卡进来有产品主线,按 F5 就退回自由创作,把链接发给同事
   *   也一样 —— 因为 `scenePreset` 走 router state 且消费后就被 `clearSceneCardState()` 清了。
   *   那个机制在 `sceneKey` 只是埋点标签时无所谓,批次 2 把它变成了**承重墙**(它决定
   *   整条产品主线渲不渲染),就必须能活过刷新。
   * ★分寸:URL 里**只放身份,不放副作用**。`clearSceneCardState` 防的是刷新重放
   *   groupPoints / ratios 那些预填 —— 那个目的保留,这里恢复的只是「我是谁」。
   */
  const sceneFromUrl = params.get("scene");
  const [sceneKey, setSceneKey] = useState<string | null>(sceneFromUrl);

  const handleSceneKeyChange = (next: string | null) => {
    setSceneKey(next);
    // 幂等:只在真的不一样时改 URL,否则 params 变更会把自己再触发一轮。
    if ((params.get("scene") ?? null) === next) return;
    const p = new URLSearchParams(params);
    if (next) p.set("scene", next);
    else p.delete("scene");
    setParams(p, { replace: true }); // replace:选个商品不该在浏览器历史里堆一层
  };

  return (
    <PosterCreateShell tab={tab} sceneKey={sceneKey}>
      <FreeCreateBody
        mode={tab === "i2i" ? "I2I" : "T2I"}
        onModeChange={onModeChange}
        initialSceneKey={sceneFromUrl}
        onSceneKeyChange={handleSceneKeyChange}
      />
    </PosterCreateShell>
  );
}
